
/// <amd-module name="Environments/CreatePanel/Components/AddEnvironmentComponent" />
import * as React from "react";

import { EnvironmentResourceType } from "DistributedTask/Client/Contracts/TaskAgent";

import { StoreManager } from "Environments/Flux/StoreManager";
import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";
import { createUrlForRoute } from "Environments/EnvironmentsCommon/Link";
import { UrlParamKeys } from "Environments/EnvironmentsCommon/Utils";
import { EnvironmentsCreatePanelStore } from "Environments/CreatePanel/Stores/EnvironmentsCreatePanelStore";
import { EnvironmentsCreatePanelActionsCreator } from "Environments/CreatePanel/ActionsCreators/EnvironmentsCreatePanelActionsCreator";
import * as Resources from "Environments/CreatePanel/Resources";
import { PageKey, Operation, getTitle, getResources } from "Environments/CreatePanel/Common/Utils";
import { EnvironmentBaseComponent, IEnvironmentBaseComponentProperties, IEnvironmentBaseComponentState } from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";
import { SelectResourceComponent } from "Environments/CreatePanel/Components/SelectResourceComponent";
import { AddResourceComponent } from "Environments/CreatePanel/Components/AddResourceComponent";
import { EnvironmentDetailsViewComponent } from "Environments/CreatePanel/Components/EnvironmentDetailsViewComponent";

import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { IVssHistoryService } from "VSS/Platform/History";

import { IButtonProps } from "VSSUI/Button";
import { Panel } from "VSSUI/Panel";
import { Page } from "VSSUI/Page";

interface IAddEnvironmentState extends IEnvironmentBaseComponentState {
    environmentName?: string;
    environmentDescription?: string;
    resourceType: EnvironmentResourceType;
    disableActionButton: boolean;
    isAddResourceView?: boolean;
    environmentId: number | undefined | null;
    createResourceSignal: boolean;
}

interface IAddEnvironmentProperties extends IEnvironmentBaseComponentProperties {
    onEnvironmentCreated: (environmentId: number) => void;
    onDismissPanel: (environmentId?: number) => void;
    instanceId: string;
}

export class AddEnvironmentComponent extends EnvironmentBaseComponent<IAddEnvironmentProperties, IAddEnvironmentState> {

    public getInstanceId(context: IVssComponentContext): string {
        return this.props.instanceId;
    }

    constructor(props: IAddEnvironmentProperties, context: IVssComponentContext) {
        super(props, context);
        const historyService = context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const historyState = historyService.getState();
        const name = historyState[UrlParamKeys.EnvironmentName];
        const description = historyState[UrlParamKeys.Description];
        const type = (historyState[UrlParamKeys.CreateNew] && parseInt(historyState[UrlParamKeys.CreateNew])) || EnvironmentResourceType.Undefined;

        delete historyState[UrlParamKeys.EnvironmentName];
        delete historyState[UrlParamKeys.Description];
        delete historyState[UrlParamKeys.CreateNew];
        historyService.replaceState(historyState);

        this.state = {
            errorMessages: {},
            disableActionButton: true,
            environmentId: undefined,
            createResourceSignal: false,
            environmentName: name,
            environmentDescription: description,
            resourceType: type as EnvironmentResourceType,
            isAddResourceView: !!name && type > 0
        };

        this._actionCreator = ActionCreatorManager.CreateActionCreator<EnvironmentsCreatePanelActionsCreator>(EnvironmentsCreatePanelActionsCreator, this._instanceId);
        this._store = StoreManager.CreateStore<EnvironmentsCreatePanelStore>(EnvironmentsCreatePanelStore, this._instanceId);
        this._store.addListener(Operation.Create, this._onEnvironmentStoreChange);
    }

    public render(): JSX.Element {
        if (this.state.isAddResourceView) {
            const queryParams: { [s: string]: string } = {};
            if (this.state.environmentName) {
                queryParams[UrlParamKeys.EnvironmentName] = this.state.environmentName;
            }

            if (this.state.environmentDescription) {
                queryParams[UrlParamKeys.Description] = this.state.environmentDescription;
            }

            if (this.state.resourceType) {
                queryParams[UrlParamKeys.CreateNew] = (this.state.resourceType as number).toString()
            }

            let urlToCurrentView = createUrlForRoute(this.context.pageContext, "ms.vss-environments-web.environments-default-route", queryParams);
            if (urlToCurrentView[0] === '/') {
                urlToCurrentView = window.location.origin + urlToCurrentView;
            }

            return (
                <AddResourceComponent
                    onResourceCreated={this._onResourceCreated}
                    onResourceCreationFailed={this._onResourceCreationFailed}
                    onDismissPanel={this._onDismissPanel}
                    onBackButtonClick={this._onBackButtonClick}
                    instanceId={this._instanceId}
                    onCreateButtonClick={this._onCreateButtonClick}
                    resourceType={this.state.resourceType}
                    environmentId={this.state.environmentId}
                    environmentName={this.state.environmentName}
                    isAddEnvironmentView={true}
                    createResourceSignal={this.state.createResourceSignal}
                    urlToCurrentView={urlToCurrentView}
                />
            );
        } else {
            return (
                <Panel titleProps={getTitle(Resources.NewEnvironment)} onDismiss={this._onDismissPanel} footerButtonProps={this._getButtonProps()}>
                    <div className="panel-content flex-grow">
                        {this.getErrorMessageView(PageKey.AddEnvironmentView)}
                        <EnvironmentDetailsViewComponent
                            instanceId={this._instanceId}
                            onEnvironmentNameChange={this._onEnvironmentNameChange}
                            onEnvironmentDescriptionChange={this._onEnvironmentDescriptionChange}
                            environmentName={this.state.environmentName}
                            environmentDescription={this.state.environmentDescription}
                        />
                        <SelectResourceComponent onSelectionResourceType={this._onSelectionResourceType} selectedResourceType={this._getSelectedIndex()} resources={getResources(this.context.pageContext)} />
                    </div>
                </Panel>
            );
        }
    }

    private _getSelectedIndex(): string | undefined {
        if (this.state.resourceType !== null && this.state.resourceType !== undefined) {
            return this.state.resourceType.toString();
        }
    }

    private _getButtonProps(): IButtonProps[] {
        let buttonsProps: IButtonProps[] = [];
        if (this.state.resourceType !== EnvironmentResourceType.Undefined) {
            buttonsProps.push({
                ariaLabel: Resources.NextButtonText,
                onClick: this._nextButtonClick,
                text: Resources.NextButtonText,
                primary: true,
                disabled: this.state.disableActionButton
            });
        } else {
            buttonsProps.push({
                ariaLabel: Resources.CreateButtonText,
                onClick: this._createWithoutResourcesButtonClick,
                text: Resources.CreateButtonText,
                primary: true,
                disabled: this.state.disableActionButton
            });
        }
        return buttonsProps;
    }

    public componentWillUnmount(): void {
        this._store.removeListener(Operation.Create, this._onEnvironmentStoreChange);
        StoreManager.DeleteStore<EnvironmentsCreatePanelStore>(EnvironmentsCreatePanelStore, this._instanceId);
        ActionCreatorManager.DeleteActionCreator<EnvironmentsCreatePanelActionsCreator>(EnvironmentsCreatePanelActionsCreator, this._instanceId);
        super.componentWillUnmount();
    }

    private _nextButtonClick = () => {
        if(this.state.resourceType === EnvironmentResourceType.VirtualMachine && this.state.environmentName) {
            this._actionCreator.createEnvironment(this.context.pageContext, this.state.environmentName, this.state.environmentDescription || "");
        }
        
        this.setState({ isAddResourceView: true });
    }

    private _onBackButtonClick = () => {
        this.setState({ isAddResourceView: false });
    }

    private _createWithoutResourcesButtonClick = () => {
        if (this.state.environmentName) {
            this._actionCreator.createEnvironment(this.context.pageContext, this.state.environmentName, this.state.environmentDescription || "", this.props.onEnvironmentCreated);
            this.setState({ disableActionButton: true });
        }
    }

    private _onSelectionResourceType = (resourceType: EnvironmentResourceType): void => {
        this.setState({ resourceType: resourceType, disableActionButton: this._getActionButtonDisableStatus(this.state.environmentName, resourceType) });
    }

    private _onCreateButtonClick = () => {
        const environmentId = this._store.getState().environment && this._store.getState().environment.id;
        if(environmentId) {
            this.setState({ environmentId: environmentId, createResourceSignal: true });
        } else {
            this._actionCreator.createEnvironment(this.context.pageContext, this.state.environmentName as string, this.state.environmentDescription || "");
            this.setState({ disableActionButton: true });
        }
    } 

    private _onDismissPanel = (): void => {
        const environmentId = this._store.getState().environment && this._store.getState().environment.id;
        this.props.onDismissPanel(environmentId);
    }

    private _getActionButtonDisableStatus(environmentName?: string, resourceType?: EnvironmentResourceType): boolean {
        return !(environmentName && environmentName.trim() !== "" && resourceType !== null && resourceType !== undefined);
    }

    private _onResourceCreated = (): void => {
        const environmentId = this._store.getState().environment.id;
        this.props.onEnvironmentCreated(environmentId);
    }

    private _onResourceCreationFailed = (): void => {
        this.setState({createResourceSignal: false });
    }

    // not called when environment is being created without resource
    private _onEnvironmentStoreChange = (): void => {
        const environmentId = this._store.getState() && this._store.getState().environment && this._store.getState().environment.id;
        this.setState({ environmentId: environmentId, createResourceSignal: true });
    }

    private _onEnvironmentNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string): void => {
        this.setState({ environmentName: newValue, disableActionButton: this._getActionButtonDisableStatus(newValue, this.state.resourceType) });
    }

    private _onEnvironmentDescriptionChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string): void => {
        this.setState({ environmentDescription: newValue });
    }

    private _store: EnvironmentsCreatePanelStore;
    private _actionCreator: EnvironmentsCreatePanelActionsCreator;

}

VssComponent.register("addEnvironment", AddEnvironmentComponent);
